import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../context/AuthContext';
import {Eye, EyeOff} from 'lucide-react';
import houseImage from '../assets/house.jpg';
import stewartLogo from '../assets/stewart_logo.png';

import {useAuthApi} from '../context/ApiContext';
import {NotificationType, useNotification} from "../context/NotificationContext.tsx";
import {isPasswordValid, PasswordRequirements, validatePassword} from "../components/PasswordRequirements.tsx";


export const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { setMe, me} = useAuth();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const authApi = useAuthApi();
    const [isLoading, setIsLoading] = useState(false);
    // set once the backend says the password was right but has expired
    const [expired, setExpired] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const validation = validatePassword(newPassword);
    const canChange = isPasswordValid(validation) && newPassword === confirmPassword;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoading(true);
        authApi.login({username, password})
            .then((response) => {
                setMe(response);
                navigate("/dashboard");
                showNotification(NotificationType.SUCCESS, 'Logged in successfully');
            })
            .catch((error) => {
                console.log("Login failed: ", error);
                const data = error?.response?.data;
                if (data?.code === 'PASSWORD_EXPIRED') {
                    setExpired(true);
                    return;
                }
                const message: string | undefined = data?.message;
                showNotification(NotificationType.ERROR, message ? message : 'Login failed. Please check your credentials.');
            })
            .finally(() => setIsLoading(false));
    };

    // changes the expired password with the one just typed, then signs in
    const handleChangeExpired = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoading(true);
        authApi.changeExpiredPassword({username, currentPassword: password, newPassword})
            .then((response) => {
                setMe(response);
                navigate("/dashboard");
                showNotification(NotificationType.SUCCESS, 'Password changed. You are signed in.');
            })
            .catch((error) => console.log("Expired password change failed: ", error))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (me) {
            //if we are already logged in, redirect to dashboard
            navigate("/dashboard");
        }
    }, [me]);


    return (
        <div className="min-h-screen flex flex-col items-center justify-start bg-cover bg-center bg-no-repeat"
             style={{ backgroundImage: `url(${houseImage})` }}
        >
            {/* Overlay to ensure text readability */}
            {/*<div className="absolute inset-0 bg-black/50" />*/}

            {/* Content container */}
            <div className="relative z-10 w-full max-w-md px-4 sm:px-6 min-h-screen py-4 sm:py-6 flex flex-col justify-between">
                {/* Logo section - improved spacing */}
                <div className="text-center pt-4 sm:pt-6">
                    <div className="flex justify-center mb-4 sm:mb-8">
                        <img
                            src={stewartLogo}
                            alt="Stewart Logo"
                            className="h-14 sm:h-16 w-auto object-contain"
                        />
                    </div>
                </div>

                {/* Login Card */}
                <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-8 mx-auto w-full">
                    <div className="mb-4 sm:mb-6 text-center">
                        <h1 className="text-xl sm:text-2xl font-bold text-blue-800">Sign In</h1>
                        <p className="text-sm text-gray-600 mt-1">Please sign in to continue</p>
                    </div>

                    {expired && (
                    <form onSubmit={handleChangeExpired} className="space-y-4 sm:space-y-6">
                        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                            Your password has expired. Choose a new one.
                        </div>

                        <div>
                            <label htmlFor="expired-new-password" className="block mb-1.5 text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <input
                                id="expired-new-password"
                                type={showPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md
                                         focus:outline-none focus:ring-2 focus:ring-blue-500
                                         focus:border-blue-500 text-sm"
                                placeholder="Enter your new password"
                            />
                            <PasswordRequirements validation={validation} />
                        </div>

                        <div>
                            <label htmlFor="expired-confirm-password" className="block mb-1.5 text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <input
                                id="expired-confirm-password"
                                type={showPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md
                                         focus:outline-none focus:ring-2 focus:ring-blue-500
                                         focus:border-blue-500 text-sm"
                                placeholder="Confirm your new password"
                            />
                            {confirmPassword && newPassword !== confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !canChange}
                            className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium
                                     rounded-md hover:bg-blue-700 focus:outline-none
                                     focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     flex items-center justify-center transition-colors"
                        >
                            {isLoading ? 'Saving...' : 'Change password and sign in'}
                        </button>

                        <button
                            type="button"
                            onClick={() => setExpired(false)}
                            className="w-full text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            Back to sign in
                        </button>
                    </form>
                    )}

                    {!expired && (
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        <div>
                            <label htmlFor="username" className="block mb-1.5 text-sm font-medium text-gray-700">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md
                                         focus:outline-none focus:ring-2 focus:ring-blue-500
                                         focus:border-blue-500 text-sm"
                                placeholder="Enter your username"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block mb-1.5 text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md
                                             focus:outline-none focus:ring-2 focus:ring-blue-500
                                             focus:border-blue-500 text-sm"
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-500" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-500" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium
                                     rounded-md hover:bg-blue-700 focus:outline-none
                                     focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     flex items-center justify-center transition-colors"
                        >
                            {isLoading ? (
                                <>
                                    <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign in'
                            )}
                        </button>

                        <div className="mt-4 sm:mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 text-gray-500 bg-white">
                                        Need help?
                                    </span>
                                </div>
                            </div>

                            {/* help links */}
                            <div className="mt-4 flex justify-between px-2">
                                <a
                                    href="https://www.unitedstatesappraisals.com/join-our-team/"
                                    target="_blank"
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                >
                                    Sign Up
                                </a>
                                <a
                                    href="/reset-password"
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                >
                                    Forgot your password?
                                </a>
                            </div>
                        </div>
                    </form>
                    )}
                </div>

                {/* Footer with some spacing at the bottom */}
                <div className="py-4 text-center text-white/90 text-sm">
                    © {new Date().getFullYear()} Stewart Valuation Intelligence
                </div>
            </div>
        </div>
    );
};